/**
 * /solo/enroll — SAA SOLO enrollment page.
 *
 *  Track A ($399/mo · PRSVR School of Record) vs. Track B ($299/mo · Charter Partnership)
 *  Grade 6-8 · State (CA / TX / FL) · Charter partner (Track B only)
 *  Stripe subscription checkout, mirrors the Sport IQ paywall pattern
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, ShieldCheck, GraduationCap, School, MapPin, Users, Check, ArrowRight,
  ArrowLeft, Trophy, Sparkles, Lock,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const SoloEnroll = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [track, setTrack] = useState('B');   // default to charter-partnership
  const [gradeLevel, setGradeLevel] = useState(7);
  const [state, setState] = useState('CA');
  const [studentFirstName, setStudentFirstName] = useState('');
  const [studentLastName, setStudentLastName] = useState('');
  const [city, setCity] = useState('');
  const [charterPartner, setCharterPartner] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, enr] = await Promise.all([
        api.get('/academy/solo/config'),
        api.get('/academy/solo/enrollment', { headers: authHeaders() }).catch(() => ({ data: null })),
      ]);
      setConfig(cfg.data);
      setEnrollment(enr.data);
    } catch { toast.error('Could not load SOLO enrollment'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll Stripe status on return from checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('solo_purchase') === 'success' && params.get('session_id')) {
      const sid = params.get('session_id');
      api.get(`/academy/solo/status/${sid}`, { headers: authHeaders() })
        .then((r) => {
          if (r.data.granted) {
            toast.success('SOLO enrollment activated — welcome to the SAA family');
            load();
          }
        })
        .catch(() => {})
        .finally(() => window.history.replaceState({}, '', '/solo/enroll'));
    }
  }, [load]);

  const startCheckout = async () => {
    if (!studentFirstName.trim() || !studentLastName.trim()) {
      toast.error("Please enter the student's first and last name");
      return;
    }
    if (!city.trim()) {
      toast.error("Please enter the student's city");
      return;
    }
    if (track === 'B' && !charterPartner.trim()) {
      toast.error('Charter partner name required for Track B');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/academy/solo/checkout', {
        track,
        grade_level: gradeLevel,
        state_of_record: state,
        student_first_name: studentFirstName.trim(),
        student_last_name: studentLastName.trim(),
        city: city.trim(),
        charter_partner: track === 'B' ? charterPartner.trim() : null,
        po_number: track === 'B' && poNumber.trim() ? poNumber.trim() : null,
        guardian_email: guardianEmail.trim() || null,
        origin_url: window.location.origin,
      }, { headers: authHeaders() });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  // Already enrolled — show status
  if (enrollment?.active) {
    const p = enrollment.pacing || {};
    const trackLabel = (config?.tracks || []).find((t) => t.key === enrollment.track)?.label || enrollment.track;
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="solo-enrolled">
        <div className="border-b-2 border-zinc-900 py-10 px-6" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.08), transparent)' }}>
          <div className="container mx-auto max-w-4xl">
            <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">You're Enrolled in SOLO</div>
            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
              Welcome, <span className="text-orange-500">Scholar Athlete</span>
            </h1>
            <p className="text-zinc-300 text-lg mt-4 max-w-2xl leading-relaxed">Your SOLO enrollment is active. Head to the Athlete Dashboard for today's Game Plan.</p>
          </div>
        </div>
        <div className="container mx-auto max-w-4xl px-6 py-10 space-y-6">
          <div className="grid md:grid-cols-3 gap-3">
            <StatCard label="Track" value={trackLabel} />
            <StatCard label="Grade Level" value={`G${enrollment.grade_level}`} />
            <StatCard label="State of Record" value={enrollment.state_of_record || '—'} />
          </div>
          <div className="border-2 border-orange-500/40 bg-orange-500/10 p-5">
            <div className="text-[10px] tracking-[0.35em] uppercase font-black text-orange-500 mb-2">Pacing</div>
            <div className="text-3xl font-black">Day {p.day_N + 1 || 1} of {p.total_days || 180}</div>
            <div className="w-full h-2 bg-zinc-900 mt-3">
              <div className="h-full bg-orange-500" style={{ width: `${p.percent_complete || 0}%` }} />
            </div>
            <p className="text-[11px] text-zinc-400 mt-3">Max unlocked lesson: {p.max_unlocked_lesson + 1 || 1} · Work-ahead cap: 1 week</p>
          </div>
          <button onClick={() => navigate('/solo/athlete')} className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2" data-testid="go-to-athlete">
            Open the Athlete Dashboard <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  const trackA = (config?.tracks || []).find((t) => t.key === 'A');
  const trackB = (config?.tracks || []).find((t) => t.key === 'B');
  const selectedTrack = track === 'A' ? trackA : trackB;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24" data-testid="solo-enroll">
      <div className="border-b-2 border-zinc-900 py-12 px-6" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.10), transparent)' }}>
        <div className="container mx-auto max-w-4xl">
          <Link to="/academy" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Academy
          </Link>
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">SAA SOLO · K-8 Online School</div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            The Sport of <span className="text-orange-500">School</span>
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
            Interest-driven, sports-themed curriculum aligned to California, Texas, and Florida state standards. 180-day academic year. Work at your pace — cap of one week ahead.
          </p>
          <div className="mt-5">
            <Link to="/solo/preview"
              className="inline-flex items-center gap-2 border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.2em]"
              data-testid="see-sample-day">
              <Sparkles className="w-3.5 h-3.5" /> See a Sample Day — No Sign-Up
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6 py-10">
        {/* Track selector */}
        <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">Step 1 · Choose Your Track</div>
        <div className="grid md:grid-cols-2 gap-3 mb-8">
          <TrackCard
            active={track === 'A'} onClick={() => setTrack('A')}
            title="PRSVR School of Record"
            price={trackA?.price}
            blurb={trackA?.blurb}
            perks={['Full admissions & transcripts', 'Official reclassification paperwork', 'State reporting (CA/TX/FL)', 'Cumulative records + graduation path']}
            testid="track-a"
          />
          <TrackCard
            active={track === 'B'} onClick={() => setTrack('B')}
            title="Charter Partnership"
            price={trackB?.price}
            blurb={trackB?.blurb}
            perks={['Curriculum + progress tracking', 'Charter-formatted CSV exports', 'Charter remains school of record', 'PO / funding invoice tracking']}
            testid="track-b"
          />
        </div>

        {/* Step 2 · details */}
        <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">Step 2 · Student Details</div>

        {/* Student identity */}
        <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="student-identity">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">Student First Name *</label>
            <input value={studentFirstName} onChange={(e) => setStudentFirstName(e.target.value)}
              placeholder="First name"
              className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none"
              data-testid="student-first-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">Student Last Name *</label>
            <input value={studentLastName} onChange={(e) => setStudentLastName(e.target.value)}
              placeholder="Last name"
              className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none"
              data-testid="student-last-name" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block flex items-center gap-1"><MapPin className="w-3 h-3" /> City *</label>
            <input value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Los Angeles"
              className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none"
              data-testid="student-city" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mb-6">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Grade Level</label>
            <div className="grid grid-cols-3 gap-2">
              {(config?.grades || [6, 7, 8]).map((g) => (
                <button key={g} onClick={() => setGradeLevel(g)}
                  className={`text-[11px] font-black uppercase tracking-widest px-2 py-3 border-2 ${gradeLevel === g ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`}
                  data-testid={`grade-${g}`}>
                  Grade {g}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block flex items-center gap-1"><MapPin className="w-3 h-3" /> State of Record</label>
            <div className="grid grid-cols-3 gap-2">
              {(config?.states || ['CA', 'TX', 'FL']).map((s) => (
                <button key={s} onClick={() => setState(s)}
                  className={`text-[11px] font-black uppercase tracking-widest px-2 py-3 border-2 ${state === s ? 'bg-orange-500 text-black border-orange-500' : 'text-zinc-400 border-zinc-800 hover:border-zinc-600'}`}
                  data-testid={`state-${s}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>

        {track === 'B' && (
          <div className="border-2 border-blue-500/40 bg-blue-500/5 p-4 mb-6" data-testid="charter-fields">
            <div className="text-[10px] tracking-[0.35em] uppercase font-black text-blue-400 mb-3 flex items-center gap-1"><Users className="w-3 h-3" /> Charter Partnership Details</div>
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">Charter Name *</label>
            <input value={charterPartner} onChange={(e) => setCharterPartner(e.target.value)} placeholder="e.g. iEP California · Method Schools · Sky Mountain" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm mb-3 focus:border-orange-500 outline-none" data-testid="charter-name" />
            <label className="text-[10px] uppercase tracking-widest font-black text-zinc-400 mb-1.5 block">PO Number (optional)</label>
            <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="Charter Purchase Order — if issued" className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none" data-testid="po-number" />
          </div>
        )}

        {/* Head Coach (Parent) inbox — sent milestone celebration emails */}
        <div className="border-2 border-zinc-800 bg-zinc-950 p-4 mb-6" data-testid="head-coach-fields">
          <div className="text-[10px] tracking-[0.35em] uppercase font-black text-orange-500 mb-3 flex items-center gap-1">Head Coach Email (Parent / Guardian)</div>
          <input type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)}
            placeholder="parent@example.com — for milestone celebrations & progress"
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2.5 text-sm focus:border-orange-500 outline-none"
            data-testid="guardian-email" />
          <div className="text-[10px] text-zinc-500 mt-2">We'll email this address when your scholar passes a Mastery Test-Out. Leave blank to send to the student's email instead.</div>
        </div>

        {/* Summary + checkout */}
        <div className="border-2 border-orange-500/60 p-5 mb-4">
          <div className="text-[10px] tracking-[0.35em] uppercase font-black text-orange-500 mb-3">Step 3 · Confirm & Enroll</div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <SummaryRow label="Student" value={`${studentFirstName || '—'} ${studentLastName || ''}`.trim() || '—'} />
            <SummaryRow label="Track" value={selectedTrack?.label} />
            <SummaryRow label="Monthly Price" value={`$${selectedTrack?.price}/mo`} />
            <SummaryRow label="Grade" value={`Grade ${gradeLevel}`} />
            <SummaryRow label="City · State" value={`${city || '—'} · ${state}`} />
            {track === 'B' && (
              <>
                <SummaryRow label="Charter" value={charterPartner || '—'} />
                <SummaryRow label="PO" value={poNumber || '—'} />
              </>
            )}
          </div>
          <button onClick={startCheckout} disabled={busy} className="w-full bg-orange-500 hover:bg-orange-400 text-black py-4 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="solo-checkout-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Continue to Secure Checkout · ${selectedTrack?.price}/mo
          </button>
          <p className="text-[10px] text-zinc-500 text-center mt-3">
            Cancel anytime. 180-day academic year. Sports-themed curriculum. Applied Mastery.
          </p>
        </div>
      </div>
    </div>
  );
};

const TrackCard = ({ active, onClick, title, price, blurb, perks, testid }) => (
  <button onClick={onClick} className={`text-left border-2 p-5 transition-all ${active ? 'border-orange-500 bg-orange-500/5' : 'border-zinc-800 hover:border-zinc-600 bg-zinc-950'}`} data-testid={testid}>
    <div className="flex items-baseline justify-between mb-2">
      <div className="text-lg font-black text-white leading-tight">{title}</div>
      <div className="text-2xl font-black text-orange-500">${price}<span className="text-xs opacity-70">/mo</span></div>
    </div>
    <p className="text-[11px] text-zinc-400 leading-relaxed">{blurb}</p>
    <ul className="mt-3 space-y-1">
      {(perks || []).map((p, i) => (
        <li key={i} className="text-[11px] text-zinc-300 flex items-start gap-1.5">
          <Check className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" /> {p}
        </li>
      ))}
    </ul>
  </button>
);

const SummaryRow = ({ label, value }) => (
  <div>
    <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">{label}</div>
    <div className="text-sm font-black text-white truncate">{value}</div>
  </div>
);

const StatCard = ({ label, value }) => (
  <div className="border-2 border-zinc-800 bg-zinc-950 p-4">
    <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">{label}</div>
    <div className="text-xl font-black text-white mt-1">{value}</div>
  </div>
);

export default SoloEnroll;
